import { Container } from '../Container';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Device } from '../../constants/device';
import { Section } from '../Section';
import { ProjectSectionProps } from '../../model/ProjectSection';
import { ElectronicDocumentationIcon } from '../icons/ElectronicDocumentManagmentIcon';
import { ProjectFeature } from '../../model/enums/ProjectFeature';
import { LaboratoryAgendasIcon } from '../icons/LaboratoryAgendasIcon';
import { CodeListsIcon } from '../icons/CodeListsIcon';
import { Colors } from '../../constants/colors';
import i18n, { t } from '../../../i18n';
import { Trans, useTranslation } from 'react-i18next';
import { getFeaturesFor } from '../../config/featuresConfig';
import { useUseCase } from '../../helpers/useUseCase';
import { PUBLISHED_WEB } from '../../config/useCasesConfig';
import { isPrerendering } from '../../helpers/isPrerendering';
import { MaximizeIcon } from '../icons/MaximizeIcon';
import { ArrowDownIcon } from '../icons/ArrowDownIcon';
import { ImplementationIcon } from '../icons/ImplementationIcon';
import { LicensingIcon } from '../icons/LicensingIcon';
import { CheckListIcon } from '../icons/CheckListIcon';
import { DeploymentIcon } from '../icons/DeploymentIcon';
import { MethodIcon } from '../icons/MethodIcon';
import { AuditReadinessIcon } from '../icons/AuditReadinessIcon';
import { AiIcon } from '../icons/AiIcon';
import { HandbookShowcase } from './Handbooks';
import { Link, useLocation } from 'react-router-dom';
import { AI_SERVICES_PATH } from '../../constants/routes';
import { localizedPath } from '../../helpers/localizedPath';
import { scrollToTop } from '../../helpers/scrollToTop';

export const Features = (props: ProjectSectionProps) => {
  const useCase = useUseCase();
  const publishedWeb = PUBLISHED_WEB[useCase];
  const getIcon = (feature: ProjectFeature) => {
    switch (feature) {
      case ProjectFeature.Documentation:
        return <ElectronicDocumentationIcon />;
      case ProjectFeature.Agendas:
        return <LaboratoryAgendasIcon />;
      case ProjectFeature.Ai:
        return <AiIcon />;
      case ProjectFeature.Codelists:
        return <CodeListsIcon />;
      case ProjectFeature.Compliance:
        return <CheckListIcon />;
      case ProjectFeature.Implementation:
        return <ImplementationIcon />;
      case ProjectFeature.Deployment:
        return <DeploymentIcon />;
      case ProjectFeature.Licensing:
        return <LicensingIcon />;
      case ProjectFeature.Methods:
        return <MethodIcon />;
      case ProjectFeature.AuditReadiness:
        return <AuditReadinessIcon />;
      default:
        return <MaximizeIcon />;
    }
  };

  const FeatureItem: React.FC<{ feature: ProjectFeature }> = ({ feature }) => {
    const { t } = useTranslation();
    const { pathname } = useLocation();
    // A use case may word a card its own way, e.g. its own norms or without method verification.
    const keyFor = (field: 'title' | 'description') => (i18n.exists(`features.${feature}.${field}For.${useCase}`) ? `features.${feature}.${field}For.${useCase}` : `features.${feature}.${field}`);
    const descriptionKey = keyFor('description');
    const [expanded, setExpanded] = useState(false);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const descRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
      if (isPrerendering()) return;
      const el = descRef.current;
      if (!el) return;

      const checkOverflow = () => {
        if (!el) return;
        const overflowing = el.scrollHeight > el.clientHeight + 1;
        setIsOverflowing(overflowing);
      };

      checkOverflow();
      window.addEventListener('resize', checkOverflow);
      return () => window.removeEventListener('resize', checkOverflow);
    }, [i18n.language]);
    return (
      <ProjectPoint expanded={expanded}>
        {getIcon(feature)}
        <h3>{t(keyFor('title'))}</h3>
        <Description ref={descRef} expanded={expanded}>
          <Trans
            i18nKey={descriptionKey}
            components={{
              p: <p />,
              ul: <ul />,
              li: <li />,
              a: <a />,
              strong: <strong />,
              // Trans drops attributes written in the translation, so internal links get their target here.
              aiServicesLink: <Link to={localizedPath(pathname, AI_SERVICES_PATH)} onClick={scrollToTop} />,
            }}
          />
        </Description>
        {isOverflowing && (
          <ReadMoreButton onClick={() => setExpanded(!expanded)} expanded={expanded} aria-expanded={expanded}>
            {expanded && <ArrowDownIcon color={Colors.primary} size={20} strokeWidth={5} />}
            <span>{expanded ? t('less') : t('more')}</span>
            {!expanded && <ArrowDownIcon color={Colors.primary} size={20} strokeWidth={5} />}
          </ReadMoreButton>
        )}
      </ProjectPoint>
    );
  };

  return (
    <Section id="features" inverse={props.inverse} heading={t('section.features')}>
      <Container>
        <FeaturesContainer>
          {getFeaturesFor(i18n.language, useCase).map((feature) => (
            <FeatureItem key={feature} feature={feature} />
          ))}
        </FeaturesContainer>
        {publishedWeb && <HandbookShowcase kind={publishedWeb} />}
      </Container>
    </Section>
  );
};

const FeaturesContainer = styled.div`
  --gap: 40px;
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  justify-content: center;
  gap: var(--gap);
  margin-top: 40px;

  & > * {
    width: calc(50% - (var(--gap) / 2));
  }

  /* An odd card out spans the row rather than sitting alone in half of it. */
  & > *:last-child:nth-child(odd) {
    width: 100%;
    min-height: 0;
  }

  ${Device.tablet} {
    --gap: 60px;
    flex-direction: column;

    & > * {
      width: 100%;
    }
  }
`;

const ProjectPoint = styled.div<{ expanded: boolean }>`
  display: flex;
  gap: 20px;
  height: auto;
  min-height: ${(p) => (p.expanded ? '0' : '360px')};
  flex-direction: column;
  align-items: start;
  padding: 25px;
  border-radius: 20px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
  background: ${Colors.background};

  > svg:first-child {
    height: 60px;
    width: auto;
  }

  ul {
    margin: 12px 0 0 0;
    padding-left: 22px;
  }

  li {
    margin: 4px 0;
    line-height: 1.6;
  }

  p {
    margin: 10px 0 14px 0; /* add a bit more space after paragraphs */
    text-align: left;
    line-height: 1.6;
  }

  h3 {
    margin: 0;
  }
`;

const Description = styled.div<{ expanded: boolean }>`
  overflow: ${(p) => (p.expanded ? 'visible' : 'hidden')};
  display: ${(p) => (p.expanded ? 'block' : '-webkit-box')};
  -webkit-box-orient: vertical;
  -webkit-line-clamp: ${(p) => (p.expanded ? 'unset' : 6)};
  line-height: 1.6;

  a {
    color: ${Colors.primary};
    text-decoration: none;
    font-weight: 700;
    font-family: inherit;
    font-size: 18px;
  }

  a:hover,
  a:focus {
    text-decoration: underline;
  }

  a::after {
    content: '↗';
    margin-left: 6px;
    font-size: 0.9em;
    text-shadow: 0 0 1px currentColor;
  }
`;

const ReadMoreButton = styled.button<{ expanded: boolean }>`
  margin-top: auto;
  background: transparent;
  border: none;
  color: ${Colors.primary};
  text-decoration: none;
  font-size: 14px;
  cursor: pointer;
  align-self: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  row-gap: 4px;

  span {
    font-weight: 700;
  }

  svg {
    transition: transform 0.2s ease-in-out;
    transform: ${(p) => (p.expanded ? 'rotate(180deg)' : 'none')};
  }
`;
